import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";

import { cn } from "@/lib/utils";

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & { previousValue?: number }
>(({ className, value, previousValue, style, ...props }, ref) => {
  const progressValue = Math.min(value || 0, 100);
  const prevValue = previousValue || 0;
  const isComplete = (value || 0) >= 100;
  
  // Check if a custom progress color is provided via CSS variable
  const customColor = style && typeof style === 'object' && '--progress-color' in style 
    ? (style as any)['--progress-color'] 
    : null;
  
  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn("relative h-4 w-full overflow-hidden rounded-full bg-secondary/50", className)}
      style={style}
      {...props}
    >
      {/* Previous progress layer (darker, distinct) */}
      {prevValue > 0 && (
        <div
          className="absolute h-full transition-all rounded-full"
          style={{ 
            width: `${Math.min(prevValue, 100)}%`,
            backgroundColor: customColor ? `${customColor}33` : undefined
          }}
        />
      )}
      
      {/* New addition layer (brighter, on top) */}
      <div
        className={cn(
          "absolute h-full transition-all rounded-full",
          !customColor && (isComplete ? "bg-accent/50" : "bg-accent/40")
        )}
        style={{ 
          width: `${progressValue}%`,
          backgroundColor: customColor ? customColor : undefined
        }}
      />
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
